import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Brain, TrendingUp, Target, Eye, BarChart3, Shield } from 'lucide-react';

const features = [
  {
    icon: Brain,
    title: 'Consultor financeiro com IA integrada',
    description: 'Inteligência artificial que aprende seus hábitos e oferece insights personalizados'
  },
  {
    icon: BarChart3,
    title: 'Análise automática de receitas e despesas',
    description: 'Categorização inteligente e detecção de padrões de gastos'
  },
  {
    icon: TrendingUp,
    title: 'Simulador de investimentos',
    description: 'Projete cenários futuros e tome decisões baseadas em dados reais'
  },
  {
    icon: Target,
    title: 'Planejamento financeiro personalizado',
    description: 'Metas adaptadas ao seu perfil e situação financeira atual'
  },
  {
    icon: Eye,
    title: 'Visualização clara de fluxo de caixa',
    description: 'Entenda exatamente para onde seu dinheiro está indo'
  },
  {
    icon: Shield,
    title: 'Relatórios estratégicos',
    description: 'Decisões embasadas com análises profundas e acessíveis'
  }
];

export const TechnicalSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section ref={ref} className="py-20 sm:py-32 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Tecnologia inteligente para{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                decisões financeiras melhores
              </span>
            </h2>
          </motion.div>

          {/* Description Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 sm:p-12 mb-16"
          >
            <p className="text-lg sm:text-xl text-foreground/90 leading-relaxed mb-6">
              O <span className="font-semibold text-primary">FinanceAI</span> é um aplicativo de gestão financeira inteligente que utiliza inteligência artificial para analisar padrões de gastos, prever cenários financeiros e auxiliar na tomada de decisões estratégicas.
            </p>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              O sistema foi desenvolvido para oferecer clareza, organização e previsibilidade financeira de forma simples, prática e acessível.
            </p>
          </motion.div>

          {/* Features Grid */}
          <div className="space-y-4">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-2xl sm:text-3xl font-bold mb-8 text-center"
            >
              Principais funcionalidades
            </motion.h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="group"
                  >
                    <div className="h-full bg-card/30 backdrop-blur-sm border border-border/50 rounded-xl p-6 hover:border-primary/30 hover:bg-card/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10">
                      <div className="flex flex-col h-full">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <h4 className="text-lg font-semibold mb-3 text-foreground">
                          {feature.title}
                        </h4>
                        <p className="text-muted-foreground leading-relaxed text-sm">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
